import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getGym, updateGym } from '../services/firebase/gym';
import { getClimber } from '../services/firebase/climber';
import { Gym } from '../models/gym/Gym';
import { GymFeedbackSettings } from '../models/climbing/RouteFeedback';
import CloseButton from '../components/CloseButton';
import '../styles/GymEdit.css';

const GymEdit: React.FC = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const [gym, setGym] = useState<Gym | null>(null);
  const [gymName, setGymName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [styles, setStyles] = useState<string[]>([]);
  
  // Feedback settings state
  const [feedbackSettings, setFeedbackSettings] = useState<GymFeedbackSettings>({
    enabled: true,
    allowStars: true,
    allowGradeSuggestions: true,
    allowComments: true,
    requireClimbToFeedback: false,
    moderateComments: false
  });
  
  // Available gym styles
  const availableStyles = ['Bouldering', 'Rope climbing'];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasEditAccess, setHasEditAccess] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGymAndCheckAccess = async () => {
      if (!gymId || !currentUser) {
        setError('Missing gym ID or user not logged in');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch gym details
        const gymData = await getGym(gymId);
        if (!gymData) {
          setError('Gym not found');
          setLoading(false);
          return;
        }
        setGym(gymData);
        
        // Set form values
        setGymName(gymData.name || '');
        setImageUrl(gymData.imageUrl || '');
        setLogoUrl(gymData.logoUrl || '');
        setStyles(gymData.styles || []);
        
        // Set feedback settings with defaults if not present
        setFeedbackSettings(gymData.feedbackSettings || {
          enabled: true,
          allowStars: true,
          allowGradeSuggestions: true,
          allowComments: true,
          requireClimbToFeedback: false,
          moderateComments: false
        });
        
        // Check if user has edit access (owner or fre role)
        const climber = await getClimber(currentUser.uid);
        if (climber) {
          const role = climber.getRoleInGym(gymId);
          setHasEditAccess(role === 'owner' || role === 'fre');
        } else {
          setHasEditAccess(false);
        }
        
        setError('');
      } catch (err: any) {
        console.error('Error fetching gym data:', err);
        setError('Failed to load gym data');
      } finally {
        setLoading(false);
      }
    };

    fetchGymAndCheckAccess();
  }, [currentUser, gymId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!gymId || !gym) {
      setError('Gym data not available');
      return;
    }
    
    if (!gymName.trim()) {
      setError('Gym name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Create updated gym object
      const updatedGym = new Gym(gymId, {
        name: gymName.trim(),
        imageUrl: imageUrl.trim() || undefined,
        logoUrl: logoUrl.trim() || undefined,
        styles: styles,
        climbing_routes: gym.climbing_routes,
        feedbackSettings
      });
      
      // Save to Firestore
      await updateGym(updatedGym);
      
      setSuccess('Gym updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
      
    } catch (err: any) {
      console.error('Error updating gym:', err);
      setError('Failed to update gym. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="gym-edit-container">
        <div className="gym-edit-header">
          <CloseButton returnTo={`/gyms/${gymId}`} />
          <h1>Edit Gym</h1>
        </div>
        <div className="loading-indicator">Loading gym data...</div>
      </div>
    );
  }

  if (!hasEditAccess) {
    return (
      <div className="gym-edit-container">
        <div className="gym-edit-header">
          <CloseButton returnTo={`/gyms/${gymId}`} />
          <h1>Edit Gym</h1>
        </div>
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to edit this gym.</p>
          <p>Only gym owners and administrators can edit gym details.</p>
          <button 
            className="back-button"
            onClick={() => navigate(`/gyms/${gymId}`)}
          >
            Return to Gym
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gym-edit-container">
      <div className="gym-edit-header">
        <CloseButton returnTo={`/gyms/${gymId}`} />
        <h1>Edit Gym</h1>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form className="gym-edit-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="gymName">Gym Name *</label>
          <input
            type="text"
            id="gymName"
            value={gymName}
            onChange={(e) => setGymName(e.target.value)}
            placeholder="Enter gym name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="imageUrl">Image URL</label>
          <input
            type="url"
            id="imageUrl"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg (optional)"
          />
          {imageUrl && (
            <div className="image-preview">
              <img 
                src={imageUrl} 
                alt="Gym preview" 
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/150?text=Invalid+URL';
                }}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="logoUrl">Logo URL</label>
          <input
            type="url"
            id="logoUrl"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.jpg (optional)"
          />
          {logoUrl && (
            <div className="logo-preview">
              <img 
                src={logoUrl} 
                alt="Logo preview" 
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/100?text=Invalid+URL';
                }}
              />
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label>Gym Styles</label>
          <div className="styles-checkboxes">
            {availableStyles.map((style) => (
              <div key={style} className="style-checkbox">
                <input
                  type="checkbox"
                  id={`style-${style}`}
                  checked={styles.includes(style)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setStyles([...styles, style]);
                    } else {
                      setStyles(styles.filter(s => s !== style));
                    }
                  }}
                />
                <label htmlFor={`style-${style}`}>{style}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Route Feedback Settings */}
        <div className="form-group">
          <label>Route Feedback Settings</label>
          <div className="feedback-settings">
            <div className="feedback-checkbox">
              <input
                type="checkbox"
                id="feedback-enabled"
                checked={feedbackSettings.enabled}
                onChange={(e) => setFeedbackSettings({
                  ...feedbackSettings,
                  enabled: e.target.checked
                })}
              />
              <label htmlFor="feedback-enabled">Enable route feedback system</label>
            </div>
            
            {feedbackSettings.enabled && (
              <div className="feedback-options">
                <div className="feedback-checkbox">
                  <input
                    type="checkbox"
                    id="allow-stars"
                    checked={feedbackSettings.allowStars}
                    onChange={(e) => setFeedbackSettings({
                      ...feedbackSettings,
                      allowStars: e.target.checked
                    })}
                  />
                  <label htmlFor="allow-stars">Allow star ratings (1-5 stars)</label>
                </div>
                
                <div className="feedback-checkbox">
                  <input
                    type="checkbox"
                    id="allow-grades"
                    checked={feedbackSettings.allowGradeSuggestions}
                    onChange={(e) => setFeedbackSettings({
                      ...feedbackSettings,
                      allowGradeSuggestions: e.target.checked
                    })}
                  />
                  <label htmlFor="allow-grades">Allow grade suggestions</label>
                </div>
                
                <div className="feedback-checkbox">
                  <input
                    type="checkbox"
                    id="allow-comments"
                    checked={feedbackSettings.allowComments}
                    onChange={(e) => setFeedbackSettings({
                      ...feedbackSettings,
                      allowComments: e.target.checked
                    })}
                  />
                  <label htmlFor="allow-comments">Allow comments</label>
                </div>
                
                <div className="feedback-checkbox">
                  <input
                    type="checkbox"
                    id="require-climb"
                    checked={feedbackSettings.requireClimbToFeedback || false}
                    onChange={(e) => setFeedbackSettings({
                      ...feedbackSettings,
                      requireClimbToFeedback: e.target.checked
                    })}
                  />
                  <label htmlFor="require-climb">Require climbing the route before giving feedback</label>
                </div>
                
                {feedbackSettings.allowComments && (
                  <div className="feedback-checkbox">
                    <input
                      type="checkbox"
                      id="moderate-comments"
                      checked={feedbackSettings.moderateComments || false}
                      onChange={(e) => setFeedbackSettings({
                        ...feedbackSettings,
                        moderateComments: e.target.checked
                      })}
                    />
                    <label htmlFor="moderate-comments">Moderate comments before publishing</label>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="cancel-button"
            onClick={() => navigate(`/gyms/${gymId}`)}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="save-button"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GymEdit;
